import { ApiError, StaleError } from '@/api/client';
import { T } from '@/i18n';

/**
 * Pure decisions behind the role selector in Bedriftstilgang (TF-ACCOUNT-ACCESS B).
 * The server is PUT /admin/users/:id/membership-role
 * (backend/controllers/admin/membershipRoleController.js): admin or user in one
 * company the caller administers, on a membership that already exists.
 */
/**
 * A role is whatever key the company has defined (docs/design/2026-09-16-custom-roles.md).
 * `admin` and `user` exist in every company and are the only two this file can name; the
 * rest are read from GET /admin/roles for the company in question.
 */
export type Role = string;

type Membership = { tenant_id: number; role?: string };

/**
 * The key the target's membership holds in one company, or null when not a member there:
 * null means no selector. The key is returned as stored; what it grants is the company's
 * role definition, which the screen reads from the server rather than deciding here.
 */
export function roleIn(memberships: Membership[] | undefined, tenantId: number): Role | null {
  const m = (memberships ?? []).find((x) => Number(x.tenant_id) === tenantId);
  if (!m) return null;
  return typeof m.role === 'string' && m.role ? m.role : 'user';
}

/** The signed-in person taking their own administrator role away, whatever they take it to. */
export const isSelfDemotion = (targetId: number, meId: number | undefined, next: Role): boolean =>
  meId != null && targetId === meId && next !== 'admin';

export const isActiveCompany = (tenantId: number, activeId: number | undefined): boolean =>
  activeId != null && tenantId === activeId;

/** The 409 both endpoints answer when a company would lose its only administrator; the tenant it names, if any. */
export function lastAdminRefusal(e: unknown): { tenant_id: number | null } | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const b = e.body as { error?: unknown; tenant_id?: unknown } | null;
  if (!b || b.error !== 'last_admin') return null;
  return { tenant_id: typeof b.tenant_id === 'number' ? b.tenant_id : null };
}

/** The 409 when a username or e-mail is already in use in any company, as a sentence; null for anything else. */
export function takenRefusal(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const b = e.body as { error?: unknown } | null;
  if (b?.error === 'username_taken') return T('Brukernavnet er allerede i bruk. Velg et annet.');
  if (b?.error === 'email_taken') return T('E-postadressen er allerede i bruk hos en annen bruker.');
  return null;
}

export type RoleFailure = {
  msg: string;
  /** the target's record is re-read so the selector follows the server */
  reread: boolean;
  /** no answer arrived: the outcome is unknown and nothing is sent again */
  unknown: boolean;
};
export function roleFailure(e: unknown): RoleFailure {
  if (e instanceof ApiError && !(e instanceof StaleError)) {
    if (lastAdminRefusal(e))
      return {
        msg: T('Dette er den eneste administratoren i bedriften. Gjør noen andre til administrator først.'),
        reread: false,
        unknown: false,
      };
    if (e.status === 403)
      return {
        msg: T('Du kan bare endre roller i bedrifter du administrerer.'),
        reread: true,
        unknown: false,
      };
    if (e.status === 404)
      return { msg: T('Personen er ikke lenger medlem i bedriften.'), reread: true, unknown: false };
    return { msg: `${T('Kunne ikke endre rolle')}: ${e.message}`, reread: true, unknown: false };
  }
  return {
    msg: T('Resultatet er ukjent. Rollen kan være endret. Ingenting ble sendt på nytt.'),
    reread: true,
    unknown: true,
  };
}
